<template>
  <div ref="wheelContainer" class="h-full w-full relative">
    <div class="h-full w-full flex-grow flex justify-center items-center">
      <div class="relative" :style="{ width: canvasSize + 'px', height: canvasSize + 'px' }">
        <!-- Visual-only grey overlay when empty; palette is never mutated -->
        <canvas ref="wheelCanvas" id="wheelCanvas" @click="spin()"
          :class="['object-contain min-w-full', { 'cursor-pointer': canSpin, 'cursor-not-allowed': !canSpin, 'filter grayscale opacity-60': !hasUsableSlices }]"
          :width="canvasSize" :height="canvasSize" :style="{ width: '100%', height: '100%' }">
        </canvas>
        <wheelOverlayText v-if="canSpin" v-on:click="spin()" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useWheelStore } from '@/stores/useWheelStore';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { useHardwareStore } from '@/stores/useHardwareStore';
import Wheel from './Wheel.js';
import wheelOverlayText from './wheelOverlayText.vue';
import * as Util from '@utils/Util.js';
import Ticker from '@utils/Ticker.js';

const wheelStore = useWheelStore();
const preferencesStore = usePreferencesStore();
const hardwareStore = useHardwareStore();

const wheelContainer = ref(null);
const wheelCanvas = ref(null);

const myWheel = ref(null);
const myTicker = ref(new Ticker());
const animationFrameID = ref(undefined);
const canvasSize = ref(700);
let resizeObserver = null;
const isFirstTick = ref(true);

const wheelConfig = computed(() => wheelStore.wheelConfig);
const darkMode = computed(() => preferencesStore.darkMode);
const version = computed(() => preferencesStore.version);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);
const fullScreen = computed(() => hardwareStore.isFullScreen);

const entries = computed(() => wheelConfig.value.entries);

const usableEntries = computed(() => {
  return (entries.value || []).filter(e =>
    e && e.enabled !== false && (
      (e.text && e.text.trim().length > 0) || e.image
    )
  );
});
const hasUsableSlices = computed(() => usableEntries.value.length > 0);
const effectiveEntries = computed(() => hasUsableSlices.value ? entries.value : [{ text: ' ' }]);
const canSpin = computed(() => hasUsableSlices.value && !wheelIsBusy.value);

onMounted(() => {
  clearCanvas();
  myWheel.value = new Wheel();
  myWheel.value.configure(wheelConfig.value, darkMode.value);
  myWheel.value.setEntries(effectiveEntries.value, wheelConfig.value.maxNames, wheelConfig.value.allowDuplicates);

  updateCanvasSize();
  resizeObserver = new ResizeObserver(updateCanvasSize);
  if (wheelContainer.value) resizeObserver.observe(wheelContainer.value);
  window.addEventListener('resize', updateCanvasSize);

  tick(0);
  startKeyListener();

});

onBeforeUnmount(() => {
  window.cancelAnimationFrame(animationFrameID.value);
  animationFrameID.value = undefined;
  if (resizeObserver) resizeObserver.disconnect();
  window.removeEventListener('resize', updateCanvasSize);
  document.removeEventListener('keydown', onKeyDown);
});

watch(wheelConfig, () => {
  if (myWheel.value) myWheel.value.configure(wheelConfig.value, darkMode.value);
});
watch(darkMode, () => {
  if (myWheel.value) myWheel.value.configure(wheelConfig.value, darkMode.value);
});
watch(entries, () => {
  if (myWheel.value) {
    // Reconfigure to ensure palette updates when transitioning between usable/non-usable slices
    myWheel.value.configure(wheelConfig.value, darkMode.value);
    myWheel.value.setEntries(
      effectiveEntries.value,
      wheelConfig.value.maxNames,
      wheelConfig.value.allowDuplicates
    );
  }
});
watch(fullScreen, () => {
  nextTick(() => updateCanvasSize());
});

const emit = defineEmits(['wheel-started', 'name-changed', 'wheel-stopped']);

function startKeyListener() {
  if (!Util.isTouchScreen()) {
    document.addEventListener('keydown', onKeyDown);
  }
}
function onKeyDown(event) {
  try {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      const target = event.target;
      if (isTextInputLike(target)) return;
      spin();
    }
  } catch (e) { }
}
function isTextInputLike(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  try { if (el.closest && el.closest('[contenteditable="true"]')) return true; } catch (_) { }
  const role = (el.getAttribute && el.getAttribute('role')) || '';
  if ((role || '').toLowerCase() === 'textbox') return true;
  return false;
}
function spin() {
  if (!hasUsableSlices.value) return;
  if (wheelIsBusy.value) return;
  wheelStore.setWheelBusy(true);
  trackInGoogleAnalytics();
  emit('wheel-started');
  myWheel.value.click(onNameChanged, onStopWheelSpin);
}
function onNameChanged() {
  emit('name-changed');
}
function onStopWheelSpin(winningEntry) {
  wheelStore.setWheelBusy(false);
  emit('wheel-stopped', winningEntry);
}
function trackInGoogleAnalytics() {
  const label = version.value;
  if (wheelConfig.value.hasOnlyDefaultEntries()) {
    Util.trackEvent('Wheel', 'SpinWithDefaultNames', label);
  } else {
    Util.trackEvent('Wheel', 'SpinWithCustomNames', label);
  }
}
function tick(ms) {
  const canvas = wheelCanvas.value || document.getElementById('wheelCanvas');
  if (canvas && myWheel.value) {
    myTicker.value.setTimestamp(ms);
    while (myTicker.value.shouldTick()) {
      myWheel.value.tick();
    }
    const context = canvas.getContext('2d', { alpha: true });
    myWheel.value.draw(context);
    if (isFirstTick.value) {
      isFirstTick.value = false;
    }
  }
  animationFrameID.value = requestAnimationFrame(tick);
}
function updateCanvasSize() {
  if (!wheelContainer.value) return;
  const container = wheelContainer.value;
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;
  let size = Math.min(containerWidth, containerHeight);
  size = Math.floor(size * 0.95);
  const maxSize = 1200;
  size = Math.min(size, maxSize);
  const minSize = 200;
  size = Math.max(size, minSize);
  if (canvasSize.value !== size) {
    canvasSize.value = size;
    nextTick(() => {
      if (wheelCanvas.value && myWheel.value) {
        const context = wheelCanvas.value.getContext('2d', { alpha: true });
        myWheel.value.refresh();
        myWheel.value.draw(context);
      }
    });
  }
}
function resetRotation() {
  if (myWheel.value) myWheel.value.resetRotation();
}
function refresh() {
  if (myWheel.value) myWheel.value.refresh();
  updateCanvasSize();
}
function clearCanvas() {
  if (wheelCanvas.value) {
    const context = wheelCanvas.value.getContext('2d', { alpha: true });
    context.clearRect(0, 0, wheelCanvas.value.width, wheelCanvas.value.height);
    if (myWheel.value && myWheel.value.wheelPainter) myWheel.value.wheelPainter.refresh();
  }
}
function forceCompleteReset() {
  clearCanvas();
  if (myWheel.value && myWheel.value.wheelPainter) myWheel.value.wheelPainter.refresh();
  refresh();
}

defineExpose({ resetRotation, refresh, forceCompleteReset, updateCanvasSize });
</script>

<style scoped></style>
